import {Component, DestroyRef, Inject, OnInit, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {FileServiceRecordModel} from '../../../models/FileServiceRecord-model';

type MatchStatus = 'MATCHED' | 'PARTIAL' | 'FAILED';

interface RecentMatch {
  filename: string;
  statusMessage: string;
  relativeTime: string;
  status: MatchStatus;
}

/**
 * Maps the backend FileServiceRecord.status string to the widget badge.
 * Add new backend values here as they appear — one line per state keeps the
 * mapping accurate and easy to extend.
 */
const STATUS_MAP: Record<string, MatchStatus> = {
  MATCHED: 'MATCHED',
  SUCCESS: 'MATCHED',
  COMPLETED: 'MATCHED',
  PARTIALLY_MATCHED: 'PARTIAL',
  PARTIAL: 'PARTIAL',
  FAILED: 'FAILED',
  NO_MATCH: 'FAILED',
};

@Component({
  selector: 'app-recent-matches',
  templateUrl: './recent-matches.component.html',
  styleUrls: ['./recent-matches.component.css']
})
export class RecentMatchesComponent implements OnInit {

  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly matches = signal<RecentMatch[]>([]);
  readonly isLoading = signal<boolean>(true);

  constructor(@Inject('API_URL') private readonly apiUrl: string) {}

  ngOnInit(): void {
    this.loadRecent();
  }

  private loadRecent(): void {
    this.isLoading.set(true);
    this.http.get<{content?: FileServiceRecordModel[]} | FileServiceRecordModel[]>(
      `${this.apiUrl}/imports/all`,
      {
        params: {
          pageNumber: 0,
          pageSize: 4,
          sortAttribute: 'importid',
          sortDirection: 'desc'
        }
      }
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const records = Array.isArray(response) ? response : (response?.content ?? []);
          this.matches.set(records.map(r => this.toMatch(r)));
          this.isLoading.set(false);
        },
        error: () => this.isLoading.set(false)
      });
  }

  private toMatch(r: FileServiceRecordModel): RecentMatch {
    const status = this.deriveStatus(r.status);
    return {
      filename: r.filename || r.blobFileName || '—',
      statusMessage: this.statusMessage(status, r.resellerprojectdetails || r.tuningCombination),
      relativeTime: this.relativeTime(r.dateOfUpload || r.dateuploaded),
      status
    };
  }

  private deriveStatus(raw: string | undefined): MatchStatus {
    if (!raw) return 'FAILED';
    const key = raw.toUpperCase().trim();
    const mapped = STATUS_MAP[key];
    if (mapped) return mapped;
    // Fallback for unmapped values: check PARTIAL first so PARTIALLY_*
    // never gets mis-classified as MATCHED by the SUCCESS/COMPLETE branch.
    if (key.includes('PARTIAL')) return 'PARTIAL';
    if (key.includes('SUCCESS') || key.includes('COMPLETE') || key.includes('MATCHED')) return 'MATCHED';
    return 'FAILED';
  }

  private statusMessage(status: MatchStatus, tuning?: string): string {
    if (status === 'MATCHED') return tuning ? `Tuned successfully — ${tuning}` : 'Tuned successfully';
    if (status === 'PARTIAL') return tuning ? `Partial tune applied — ${tuning}` : 'Partial tune applied';
    return 'No tune available';
  }

  private relativeTime(date: string | undefined): string {
    if (!date) return '';
    const ms = Date.parse(date);
    if (isNaN(ms)) return date;
    const diff = Date.now() - ms;
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.floor(hr / 24);
    return `${day} day${day === 1 ? '' : 's'} ago`;
  }

  viewAll(): void {
    this.router.navigate(['/app/file-service']);
  }
}
